import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../config/axiosConfig";
import config from "../../config/api";

const BackendURL = config.backendUrl;

export const fetchGroups = createAsyncThunk(
  "groups/fetchGroups",
  async (_, { getState, rejectWithValue }) => {
    const { group } = getState();
    if (group.lastFetch && Date.now() - group.lastFetch < 5000) {
      return group.group;
    }
    try {
      const response = await axiosInstance.get("/groups");
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createOrUpdateGroup = createAsyncThunk(
  "groups/createOrUpdateGroup",
  async ({ newGroupName, servicetype }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/groups/all", {
        newGroupName,
        servicetype,
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateGroup = createAsyncThunk(
  "groups/updateGroup",
  async ({ id, newGroupName, servicetype }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put("/groups/update-group", {
        id,
        newGroupName,
        servicetype,
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createGroup = createAsyncThunk(
  "groups/createGroup",
  async (groupData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/groups", {
        name: groupData.name,
        servicetype: groupData.servicetype || '' // Include servicetype
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteGroup = createAsyncThunk(
  "groups/deleteGroup",
  async (id, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`/groups/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchEmployeeAuthDataGroup = createAsyncThunk(
  "groups/fetchEmployeeAuthDataGroup",
  async (_, { rejectWithValue }) => {
    try {
      const loggedInUserEmail = (localStorage.getItem("name") || "").trim();
      const userRole = localStorage.getItem("userRole") || localStorage.getItem("auth");
      if (!loggedInUserEmail) throw new Error("No user email found");
      const response = await axiosInstance.get(`${BackendURL}/employees`);
      const employees = response.data;
      const loginEmail = (localStorage.getItem("email") || "").trim().toLowerCase();
      const matchingEmployee =
        employees.find(
          (employee) =>
            String(employee.EmpFirstName || "").trim() === loggedInUserEmail
        ) ||
        (loginEmail
          ? employees.find(
              (employee) =>
                String(employee.EmpEmail || "").trim().toLowerCase() === loginEmail
            )
          : undefined);
      if (!matchingEmployee) throw new Error("Employee not found");
      return {
        empGroups: matchingEmployee.EmpGroups,
        authLevel: userRole,
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);
const groupSlice = createSlice({
  name: "group",
  initialState: {
    group: [],
    empGroupsL3: [],
    authLevel: null,
    loading: false,
    error: null,
    lastFetch: null,
  },

  reducers: {}, // No synchronous reducers needed
  extraReducers: (builder) => {
    builder

    .addCase(fetchGroups.fulfilled, (state, action) => {
        state.loading = false;
        state.lastFetch = Date.now(); // Set the timestamp when we got fresh data
        const allGroups = action.payload;
        if (state.authLevel === "L3" && Array.isArray(state.empGroupsL3)) {
          state.group = allGroups.filter((group) =>
            state.empGroupsL3.includes(group?._id || group?.id)
          );
        } else {
          state.group = allGroups;
        }
      })
      .addCase(fetchGroups.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(createGroup.fulfilled, (state, action) => {
        state.group.push(action.payload);
      })

      .addCase(updateGroup.fulfilled, (state, action) => {
        const index = state.group.findIndex((g) => g.id === action.payload.id);
        if (index !== -1) state.group[index] = action.payload;
      })

      .addCase(deleteGroup.fulfilled, (state, action) => {
        state.group = state.group.filter(
          (group) => group.id !== action.payload
        );
      })
      .addCase(fetchEmployeeAuthDataGroup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEmployeeAuthDataGroup.fulfilled, (state, action) => {
        state.loading = false;
        state.empGroupsL3 = action.payload.empGroups;
        state.authLevel = action.payload.authLevel; // ← add this line
      })
      .addCase(fetchEmployeeAuthDataGroup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })

      .addCase(createOrUpdateGroup.fulfilled, (state, action) => {
        const newOrUpdatedGroup = action.payload;
        const index = state.group.findIndex(g => g.id === newOrUpdatedGroup.id);

        if (index !== -1) {
          state.group[index] = newOrUpdatedGroup;
        } else {
          state.group.push(newOrUpdatedGroup);
        }
      })
      .addCase(createOrUpdateGroup.rejected, (state, action) => {
        state.error = action.payload || action.error.message;
      });
  },
});

export default groupSlice.reducer;
